import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Download,
  Search,
  Filter,
  Users,
  X,
  Instagram,
  Youtube,
  CheckCircle,
  Ban,
  UserCheck,
  Eye,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaTiktok } from 'react-icons/fa';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to export to Excel/CSV
const exportToExcel = (data, filename) => {
  const headers = [
    'E-posta', 'Ad Soyad', 'Telefon', 'Şehir', 'Doğum Tarihi', 'IBAN',
    'Instagram Kullanıcı Adı', 'Instagram Takipçi', 'Instagram Doğrulandı',
    'TikTok Kullanıcı Adı', 'TikTok Takipçi', 'TikTok Doğrulandı',
    'YouTube Kullanıcı Adı', 'YouTube Abone', 'YouTube Doğrulandı',
    'Katıldığı Kampanyalar', 'Kayıt Tarihi'
  ];
  
  const rows = data.map(user => [
    user.email || '',
    user.full_name || '',
    user.phone || '',
    user.city || '',
    user.birth_date || '',
    user.iban || '',
    user.instagram_username || '',
    user.instagram_followers || 0,
    user.instagram_username_verified ? 'Evet' : 'Hayır',
    user.tiktok_username || '',
    user.tiktok_followers || 0,
    user.tiktok_username_verified ? 'Evet' : 'Hayır',
    user.youtube_username || '',
    user.youtube_followers || 0,
    user.youtube_username_verified ? 'Evet' : 'Hayır',
    (user.campaigns || []).map(c => c.title).join(', '),
    user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : ''
  ]);
  
  const BOM = '\uFEFF';
  const csvContent = BOM + [
    headers.join(';'),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 'Ardahan', 'Artvin',
  'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur',
  'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan',
  'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 'Kırklareli', 'Kırşehir',
  'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Mardin', 'Mersin', 'Muğla', 'Muş',
  'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas',
  'Şanlıurfa', 'Şırnak', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak', 'KKTC'
];

export default function AdminUsers() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignParticipants, setCampaignParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [searchName, setSearchName] = useState('');
  const [filterCity, setFilterCity] = useState('all');
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [filterMinFollowers, setFilterMinFollowers] = useState('');
  const [filterBanned, setFilterBanned] = useState('all');
  const [filterActive, setFilterActive] = useState('all'); // Aktif/Pasif/Tümü
  
  // Stats
  const [totalUsers, setTotalUsers] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('campaigns')
        .select('id, title')
        .order('title');
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      // Fetch all profiles via backend API (bypasses RLS)
      const profilesResponse = await fetch(`${BACKEND_URL}/api/admin/profiles`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      
      if (!profilesResponse.ok) throw new Error('Failed to fetch profiles');
      const profilesData = await profilesResponse.json();
      
      // Fetch campaign participants via backend API
      const participantsResponse = await fetch(`${BACKEND_URL}/api/admin/campaign-participants`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      
      let participantsData = [];
      if (participantsResponse.ok) {
        participantsData = await participantsResponse.json();
      }
      
      setCampaignParticipants(participantsData);
      setAllUsers(profilesData);
      setTotalUsers(profilesData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  // Apply filters whenever allUsers or filters change
  useEffect(() => {
    let filtered = [...allUsers];

    // Email filter
    if (searchEmail) {
      filtered = filtered.filter(p => 
        p.email?.toLowerCase().includes(searchEmail.toLowerCase())
      );
    }

    // Name filter
    if (searchName) {
      filtered = filtered.filter(p => 
        p.full_name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    // City filter
    if (filterCity !== 'all') {
      filtered = filtered.filter(p => 
        p.city?.toLowerCase() === filterCity.toLowerCase()
      );
    }

    // Active/Passive filter
    if (filterActive === 'active') {
      filtered = filtered.filter(p => p.is_active === true);
    } else if (filterActive === 'passive') {
      filtered = filtered.filter(p => p.is_active === false);
    }

    // Banned filter
    if (filterBanned === 'banned') {
      filtered = filtered.filter(p => p.is_banned === true);
    } else if (filterBanned === 'not_banned') {
      filtered = filtered.filter(p => !p.is_banned);
    }

    // Platform filter - show users who have filled in username AND followers for that platform
    if (filterPlatform === 'instagram') {
      filtered = filtered.filter(p => p.instagram_username && (p.instagram_followers || p.instagram_followers === 0));
    } else if (filterPlatform === 'tiktok') {
      filtered = filtered.filter(p => p.tiktok_username && (p.tiktok_followers || p.tiktok_followers === 0));
    } else if (filterPlatform === 'youtube') {
      filtered = filtered.filter(p => p.youtube_username && (p.youtube_followers || p.youtube_followers === 0));
    }

    // Verified filter - works with selected platform if any
    if (filterVerified === 'verified') {
      if (filterPlatform === 'instagram') {
        filtered = filtered.filter(p => p.instagram_username_verified);
      } else if (filterPlatform === 'tiktok') {
        filtered = filtered.filter(p => p.tiktok_username_verified);
      } else if (filterPlatform === 'youtube') {
        filtered = filtered.filter(p => p.youtube_username_verified);
      } else {
        // No platform selected - at least one platform verified
        filtered = filtered.filter(p => 
          p.instagram_username_verified || p.tiktok_username_verified || p.youtube_username_verified
        );
      }
    } else if (filterVerified === 'unverified') {
      if (filterPlatform === 'instagram') {
        filtered = filtered.filter(p => p.instagram_username && !p.instagram_username_verified);
      } else if (filterPlatform === 'tiktok') {
        filtered = filtered.filter(p => p.tiktok_username && !p.tiktok_username_verified);
      } else if (filterPlatform === 'youtube') {
        filtered = filtered.filter(p => p.youtube_username && !p.youtube_username_verified);
      } else {
        // No platform selected - none verified
        filtered = filtered.filter(p => 
          !p.instagram_username_verified && !p.tiktok_username_verified && !p.youtube_username_verified
        );
      }
    }

    // Min followers filter - works with selected platform if any
    if (filterMinFollowers) {
      const minCount = parseInt(filterMinFollowers);
      if (filterPlatform === 'instagram') {
        filtered = filtered.filter(p => (p.instagram_followers || 0) >= minCount);
      } else if (filterPlatform === 'tiktok') {
        filtered = filtered.filter(p => (p.tiktok_followers || 0) >= minCount);
      } else if (filterPlatform === 'youtube') {
        filtered = filtered.filter(p => (p.youtube_followers || 0) >= minCount);
      } else {
        // No platform selected - any platform with min followers
        filtered = filtered.filter(p => {
          const max = Math.max(
            p.instagram_followers || 0,
            p.tiktok_followers || 0,
            p.youtube_followers || 0
          );
          return max >= minCount;
        });
      }
    }

    // Add campaign info to each user
    const usersWithCampaigns = filtered.map(user => {
      const userParticipations = campaignParticipants.filter(p => p.user_id === user.id);
      const userCampaigns = userParticipations.map(p => {
        const campaign = campaigns.find(c => c.id === p.campaign_id);
        return campaign || { id: p.campaign_id, title: 'Unknown' };
      });
      return { ...user, campaigns: userCampaigns };
    });

    // Campaign filter
    let finalFiltered = usersWithCampaigns;
    if (filterCampaign !== 'all') {
      finalFiltered = usersWithCampaigns.filter(user => 
        user.campaigns.some(c => c.id === filterCampaign)
      );
    }

    setUsers(finalFiltered);
    setFilteredCount(finalFiltered.length);
  }, [allUsers, campaignParticipants, campaigns, searchEmail, searchName, filterCity, filterCampaign, filterPlatform, filterVerified, filterMinFollowers, filterBanned, filterActive]);

  useEffect(() => {
    fetchCampaigns();
    fetchData();
  }, [fetchCampaigns, fetchData]);

  const clearFilters = () => {
    setSearchEmail('');
    setSearchName('');
    setFilterCity('all');
    setFilterCampaign('all');
    setFilterPlatform('all');
    setFilterVerified('all');
    setFilterMinFollowers('');
    setFilterBanned('all');
    setFilterActive('all');
  };

  // Ban/Unban user
  const [banReason, setBanReason] = useState('');
  // The reason is shown to the banned user; the backend refuses a ban without one.
  const MIN_BAN_REASON = 5;
  const banReasonValid = banReason.trim().length >= MIN_BAN_REASON;

  const handleBanUser = async (userId, currentBanStatus) => {
    try {
      const newBanStatus = !currentBanStatus;
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/ban`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_banned: newBanStatus, ban_reason: newBanStatus ? banReason.trim() : null })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || 'İşlem sırasında hata oluştu');

      // Update local state
      setAllUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, is_banned: newBanStatus, ban_reason: result.ban_reason } : user
      ));

      toast.success(newBanStatus ? 'Kullanıcı banlandı' : 'Kullanıcı banı kaldırıldı');
    } catch (error) {
      console.error('Error updating ban status:', error);
      toast.error(error.message || 'İşlem sırasında hata oluştu');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      exportToExcel(users, 'appstars_kullanicilar');
      toast.success(`${users.length} kullanıcı Excel olarak indirildi`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Dışa aktarma sırasında hata oluştu');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = searchEmail || searchName || filterCity !== 'all' || 
    filterCampaign !== 'all' || filterPlatform !== 'all' || 
    filterVerified !== 'all' || filterMinFollowers || filterBanned !== 'all' || filterActive !== 'all';

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-brand-ink" />
          <h2 className="font-brand text-2xl text-brand-ink">Kullanıcı Listesi</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {filteredCount} / {totalUsers} kullanıcı
          </span>
          <Button
            onClick={handleExport}
            disabled={exporting || users.length === 0}
            className=""
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'İndiriliyor...' : 'Excel İndir'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filtreler
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Temizle
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="py-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {/* Email Search */}
            <div>
              <Label className="text-xs">E-posta</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Ara..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="search-email"
                />
              </div>
            </div>

            {/* Name Search */}
            <div>
              <Label className="text-xs">Ad Soyad</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Ara..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="search-name"
                />
              </div>
            </div>

            {/* City Filter */}
            <div>
              <Label className="text-xs">Şehir</Label>
              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger className="h-9 mt-1" data-testid="filter-city">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {CITIES.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Filter */}
            <div>
              <Label className="text-xs">Kampanya</Label>
              <Select value={filterCampaign} onValueChange={setFilterCampaign}>
                <SelectTrigger className="h-9 mt-1" data-testid="filter-campaign">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {campaigns.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>{campaign.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Platform Filter */}
            <div>
              <Label className="text-xs">Platform</Label>
              <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                <SelectTrigger className="h-9 mt-1" data-testid="filter-platform">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Verified Filter */}
            <div>
              <Label className="text-xs">Doğrulama</Label>
              <Select value={filterVerified} onValueChange={setFilterVerified}>
                <SelectTrigger className="h-9 mt-1" data-testid="filter-verified">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="verified">Doğrulanmış</SelectItem>
                  <SelectItem value="unverified">Doğrulanmamış</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Min Followers */}
            <div>
              <Label className="text-xs">Min Takipçi</Label>
              <Input
                type="number"
                placeholder="0"
                value={filterMinFollowers}
                onChange={(e) => setFilterMinFollowers(e.target.value)}
                className="h-9 mt-1"
                data-testid="filter-min-followers"
              />
            </div>

            {/* Active Status Filter */}
            <div>
              <Label className="text-xs">Hesap Durumu</Label>
              <Select value={filterActive} onValueChange={setFilterActive}>
                <SelectTrigger className="h-9 mt-1" data-testid="filter-active">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="passive">Pasif (Silinmiş)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ban Status Filter */}
            <div>
              <Label className="text-xs">Ban Durumu</Label>
              <Select value={filterBanned} onValueChange={setFilterBanned}>
                <SelectTrigger className="h-9 mt-1" data-testid="filter-banned">
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="banned">Banlı</SelectItem>
                  <SelectItem value="not_banned">Bansız</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <ScrollArea className="h-[calc(100vh-340px)]">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Kullanıcı bulunamadı</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>Şehir</TableHead>
                  <TableHead>Instagram</TableHead>
                  <TableHead>TikTok</TableHead>
                  <TableHead>YouTube</TableHead>
                  <TableHead>Kampanyalar</TableHead>
                  <TableHead>İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user.id} 
                    data-testid={`user-row-${user.id}`}
                    className={!user.is_active ? 'bg-gray-50 opacity-60' : ''}
                  >
                    <TableCell className="font-medium text-sm">{user.email}</TableCell>
                    <TableCell className="text-sm">
                      {user.is_active ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Aktif
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-200">
                          Pasif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        {user.full_name || '-'}
                        {user.full_name_verified && (
                          <CheckCircle className="w-3 h-3 text-green-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{user.city || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {user.instagram_username ? (
                        <div className="flex items-center gap-1">
                          <Instagram className="w-4 h-4 text-pink-500" />
                          <span>@{user.instagram_username}</span>
                          <span className="text-gray-500">({(user.instagram_followers || 0).toLocaleString()})</span>
                          {user.instagram_username_verified && (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.tiktok_username ? (
                        <div className="flex items-center gap-1">
                          <FaTiktok className="w-4 h-4" />
                          <span>@{user.tiktok_username}</span>
                          <span className="text-gray-500">({(user.tiktok_followers || 0).toLocaleString()})</span>
                          {user.tiktok_username_verified && (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.youtube_username ? (
                        <div className="flex items-center gap-1">
                          <Youtube className="w-4 h-4 text-red-500" />
                          <span>@{user.youtube_username}</span>
                          <span className="text-gray-500">({(user.youtube_followers || 0).toLocaleString()})</span>
                          {user.youtube_username_verified && (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-wrap gap-1">
                        {user.campaigns?.length > 0 ? (
                          user.campaigns.slice(0, 2).map(c => (
                            <Badge key={c.id} variant="secondary" className="text-xs">
                              {c.title}
                            </Badge>
                          ))
                        ) : '-'}
                        {user.campaigns?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{user.campaigns.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <AlertDialog onOpenChange={(open) => { if (open) setBanReason(''); }}>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant={user.is_banned ? "outline" : "destructive"}
                            size="sm"
                            className={user.is_banned ? "border-green-500 text-green-600 hover:bg-green-50" : ""}
                            data-testid={`ban-btn-${user.id}`}
                          >
                            {user.is_banned ? (
                              <>
                                <UserCheck className="w-4 h-4 mr-1" />
                                Banı Kaldır
                              </>
                            ) : (
                              <>
                                <Ban className="w-4 h-4 mr-1" />
                                Banla
                              </>
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {user.is_banned ? 'Kullanıcı Banını Kaldır' : 'Kullanıcıyı Banla'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {user.is_banned 
                                ? `${user.email} adresli kullanıcının banını kaldırmak istediğinize emin misiniz? Kullanıcı tekrar giriş yapabilecek.`
                                : `${user.email} adresli kullanıcıyı banlamak istediğinize emin misiniz? Açık oturumu kapanır ve tekrar giriş yapamaz.`
                              }
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          {!user.is_banned && (
                            <div className="space-y-2 rounded-lg border-2 border-brand-red bg-brand-red-soft p-3">
                              <Label htmlFor={`ban-reason-${user.id}`} className="text-brand-red-deep font-semibold">
                                Ban sebebi <span aria-hidden="true">*</span>
                              </Label>
                              <Textarea
                                id={`ban-reason-${user.id}`}
                                value={banReason}
                                onChange={(e) => setBanReason(e.target.value)}
                                maxLength={500}
                                rows={3}
                                autoFocus
                                required
                                className="bg-white"
                                placeholder="Örn. Sahte etkileşim tespit edildi."
                                data-testid={`ban-reason-${user.id}`}
                              />
                              <p className="text-xs text-brand-red-deep">
                                Zorunlu. Kullanıcı giriş ekranında bu sebebi görür.
                                {!banReasonValid && ` En az ${MIN_BAN_REASON} karakter yazın.`}
                              </p>
                            </div>
                          )}
                          <AlertDialogFooter>
                            <AlertDialogCancel>İptal</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleBanUser(user.id, user.is_banned)}
                              disabled={!user.is_banned && !banReasonValid}
                              className={user.is_banned ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
                            >
                              {user.is_banned ? 'Banı Kaldır' : 'Banla'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                        data-testid={`view-user-${user.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Detay
                      </Button>
                      {user.is_banned && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          Banlı
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}
